import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useResetPassword } from '@/hooks/usePasswordReset'
import { BetaTag } from '@/components/ui/BetaTag'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'

export function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [error, setError] = useState('')

  const resetPassword = useResetPassword()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== passwordConfirmation) {
      setError(t('reset_password.passwords_not_match'))
      return
    }

    try {
      await resetPassword.mutateAsync({
        token,
        password,
        password_confirmation: passwordConfirmation,
      })
      navigate('/login', { replace: true })
    } catch (err: unknown) {
      // EP-144 C-9 - this page is also the registration verification landing.
      // api/app/controllers/api/passwords_controller.rb renders
      // { error: { code, message, details } }, so reading `data.errors` alone
      // showed the generic invalid-token string for every real failure
      // (weak password, mismatched confirmation). Prefer the server message,
      // keep the legacy array as a fallback.
      const payload = (err as {
        response?: { data?: { error?: { code?: string; message?: string }; errors?: string[] } }
      }).response?.data
      setError(
        payload?.error?.message ||
          payload?.errors?.join(', ') ||
          t('reset_password.invalid_token'),
      )
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Card className="w-full max-w-md shadow-xl" bodyClassName="text-center">
            <h1 className="text-2xl font-bold text-error">{t('reset_password.invalid_token')}</h1>
            <p className="text-base-content/60">{t('reset_password.invalid_token')}</p>
            <Button className="mt-4" onClick={() => navigate('/login')}>
              {t('login.submit')}
            </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <Card className="w-full max-w-md shadow-xl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-primary inline-flex items-center justify-center gap-2">
              FlowRent
              <BetaTag />
            </h1>
            <p className="text-base-content/60">{t('reset_password.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="error" icon={null}>
                <span>{error}</span>
              </Alert>
            )}

            <Input
              label={t('reset_password.password')}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
            />

            <Input
              label={t('reset_password.confirm_password')}
              type="password"
              placeholder="••••••••"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              minLength={6}
              required
            />

            <Button
              type="submit"
              block
              disabled={resetPassword.isPending}
              loading={resetPassword.isPending}
            >
              {t('reset_password.submit')}
            </Button>
          </form>
      </Card>
    </div>
  )
}
